import React from 'react';
import { Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { WeeklyHours, isBusinessOpen, getNextOpeningTime } from '@communitly/shared';
import { useTimeFormatParameters } from '@/hooks/useParameters';
import { useTranslation } from 'react-i18next';

interface OpeningHoursTextProps {
  openingHours: WeeklyHours | null;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  showIcon?: boolean;
  showEnhancedStatus?: boolean; // If true, shows "Opens Monday at 9AM" when closed
}

const OpeningHoursText: React.FC<OpeningHoursTextProps> = ({
  openingHours,
  className,
  size = 'sm',
  showIcon = true,
  showEnhancedStatus = false
}) => {
  const { t } = useTranslation();
  const { timeDisplayFormat } = useTimeFormatParameters();

  if (!openingHours) {
    return (
      <div className={cn("flex items-center gap-1.5", className)}>
        {showIcon && <Clock className="h-3 w-3 text-gray-400" />}
        <span className={cn(
          "text-gray-500",
          size === 'sm' && "text-xs",
          size === 'md' && "text-sm", 
          size === 'lg' && "text-base"
        )}>
          {t('service.card.hours_na')}
        </span>
      </div>
    );
  }

  // Get opening status
  const openingStatus = isBusinessOpen(openingHours, timeDisplayFormat, t);
  
  // Enhanced status with next opening time when closed
  const getEnhancedOpeningStatus = () => {
    if (openingStatus.isOpen) {
      return openingStatus; // Already shows "Open now • Closes at 6PM"
    }
    
    // If closed, get next opening time
    const nextOpeningTime = getNextOpeningTime(openingHours, timeDisplayFormat, t);
    return {
      isOpen: false,
      status: nextOpeningTime // "Opens Monday at 9AM"
    };
  };

  const displayStatus = showEnhancedStatus ? getEnhancedOpeningStatus() : openingStatus;

  // Icon size based on text size
  const iconSize = {
    sm: "h-3 w-3",
    md: "h-4 w-4", 
    lg: "h-5 w-5"
  }[size];

  // Text size classes
  const textSize = {
    sm: "text-xs",
    md: "text-sm",
    lg: "text-base"
  }[size];

  return (
    <div className={cn("flex items-center gap-1.5", className)}>
      {showIcon && (
        <Clock className={cn(iconSize, "text-gray-500 flex-shrink-0")} />
      )}
      <span className={cn(
        "font-medium",
        textSize,
        displayStatus.isOpen ? "text-green-700" : "text-red-600"
      )}>
        {displayStatus.status}
      </span>
    </div>
  );
};

export default OpeningHoursText;